import React, { useState } from 'react';
import { Box, Typography, Modal } from '@mui/material';
import { keyframes } from '@mui/system';
import logo from '../asserts/logo/whitelogo.svg';

// Keyframe animation for logo pulsing/glowing effect
const logoGlow = keyframes`
  0%, 100% {
    opacity: 0.7;
    filter: drop-shadow(0 0 10px rgba(0, 148, 251, 0.3));
  }
  50% {
    opacity: 1;
    filter: drop-shadow(0 0 20px rgba(0, 148, 251, 0.8));
  }
`;

// Keyframe animation for logo scaling
const logoScale = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Keyframe animation for text pulsing
const textPulse = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
`;

/**
 * LoadingOverlay Component
 * Displays an animated loading overlay with ValidAIte logo image or text fallback
 *
 * @param {boolean} open - Controls visibility of the overlay
 * @param {string} message - Optional loading message
 */
const LoadingOverlay = ({ open, message = "Processing..." }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Modal
      open={open}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none'
        }}
      >
        {/* Logo Container */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          {!imageError ? (
            /* ValidAIte Logo Image with Animation */
            <Box
              component="img"
              src={logo}
              alt="ValidAIte Logo"
              onError={() => setImageError(true)}
              sx={{
                height: '80px',
                width: 'auto',
                animation: `${logoGlow} 2s ease-in-out infinite, ${logoScale} 2s ease-in-out infinite`,
                transition: 'all 0.3s ease'
              }}
            />
          ) : (
            /* Fallback to styled text logo if image doesn't load */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Check Icon */}
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  backgroundColor: '#0094FB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: `${logoScale} 2s ease-in-out infinite`,
                  boxShadow: '0 0 20px rgba(0, 148, 251, 0.5)'
                }}
              >
                <Typography sx={{ fontSize: '32px', color: 'white', fontWeight: 700 }}>
                  ✓
                </Typography>
              </Box>

              {/* ValidAIte Text */}
              <Typography
                sx={{
                  fontSize: '48px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #0094FB 0%, #00C4FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: `${textPulse} 2s ease-in-out infinite`,
                  letterSpacing: '2px'
                }}
              >
                Valid<span style={{ color: '#00C4FF' }}>AI</span>te
              </Typography>
            </Box>
          )}
        </Box>

        {/* Loading Message */}
        <Typography
          sx={{
            color: '#E2E8F0',
            fontSize: '18px',
            fontWeight: 500,
            mt: 2,
            opacity: 0.9,
            animation: `${keyframes`
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            `} 1.5s ease-in-out infinite`
          }}
        >
          {message}
        </Typography>

        {/* Loading Dots */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#0094FB',
                animation: `${keyframes`
                  0%, 100% { transform: scale(0.8); opacity: 0.5; }
                  50% { transform: scale(1.2); opacity: 1; }
                `} 1s ease-in-out infinite`,
                animationDelay: `${index * 0.2}s`
              }}
            />
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default LoadingOverlay;
